/**
 * Image Server Configuration
 * 
 * Centralizes configuration for connecting to the image server
 * that provides character portraits, adventure title images, and banners.
 */

// Cache for image server URL to avoid repeated calculations and logging
let cachedImageServerUrl: string | null = null;
let imageServerConfigInitialized = false;

// Helper to get image server URL - prioritize environment variable, but fix protocol mismatch
const getImageServerUrl = (): string => {
  // Return cached value if available
  if (cachedImageServerUrl !== null) {
    return cachedImageServerUrl;
  }
  
  // Get current protocol
  const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
  const expectedScheme = currentProtocol === 'https:' ? 'https' : 'http';
  
  // Check environment variable
  const envUrl = import.meta.env.VITE_IMAGE_SERVER_URL;
  
  if (envUrl && typeof envUrl === 'string' && envUrl.trim() !== '') {
    let finalUrl = envUrl;
    
    // If environment URL scheme doesn't match current protocol, fix it
    const envScheme = envUrl.startsWith('https://') ? 'https' : 'http';
    if (envScheme !== expectedScheme && typeof window !== 'undefined') {
      if (!imageServerConfigInitialized || import.meta.env.DEV) {
        console.warn(`[Config] Image server protocol mismatch: env has ${envScheme}:// but page is ${expectedScheme}://, fixing URL`);
      }
      finalUrl = finalUrl.replace(/^https?:\/\//, `${expectedScheme}://`);
    }
    
    // If URL contains localhost and we're in browser, replace with current hostname
    // This allows the app to work when accessed from phone/network IP
    if (typeof window !== 'undefined' && finalUrl.includes('localhost')) {
      const currentHostname = window.location.hostname;
      // Only replace if we're not already on localhost (to avoid breaking local development)
      if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1') {
        if (!imageServerConfigInitialized || import.meta.env.DEV) {
          console.log(`[Config] Replacing localhost with current hostname: ${currentHostname}`);
        }
        finalUrl = finalUrl.replace(/localhost/g, currentHostname);
      }
    }
    
    if (!imageServerConfigInitialized || import.meta.env.DEV) {
      console.log('[Config] Using environment variable for image server URL:', finalUrl);
    }
    
    cachedImageServerUrl = finalUrl;
    imageServerConfigInitialized = true;
    return finalUrl;
  }
  
  // Fallback: derive from current page location (workspace setup: image server is port + 2 from frontend)
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const currentPort = window.location.port;
    
    if (currentPort && import.meta.env.DEV) {
      const portNum = parseInt(currentPort, 10);
      // Check if this looks like a workspace port (3020, 3010, etc.)
      if (portNum >= 3000 && portNum < 3100 && portNum % 10 === 0) {
        const imageServerPort = portNum + 2; // Image server is typically port + 2
        const fallbackUrl = `${protocol}//${window.location.hostname}:${imageServerPort}`;
        if (!imageServerConfigInitialized || import.meta.env.DEV) {
          console.log('[Config] Derived image server URL from port:', fallbackUrl);
        }
        cachedImageServerUrl = fallbackUrl;
        imageServerConfigInitialized = true;
        return fallbackUrl;
      }
    }
    
    // Use protocol-aware default
    const fallbackUrl = `${protocol}//${window.location.hostname}:3002`;
    if (!imageServerConfigInitialized || import.meta.env.DEV) {
      console.log('[Config] Using protocol-aware fallback for image server URL:', fallbackUrl);
    }
    cachedImageServerUrl = fallbackUrl;
    imageServerConfigInitialized = true;
    return fallbackUrl;
  }
  
  // Final fallback
  if (!imageServerConfigInitialized || import.meta.env.DEV) {
    console.log('[Config] Using final fallback for image server URL: http://localhost:3002');
  }
  cachedImageServerUrl = 'http://localhost:3002';
  imageServerConfigInitialized = true;
  return 'http://localhost:3002';
};

export const imageServerConfig = {
  /**
   * Base URL for the image server
   */
  get baseUrl() {
    return getImageServerUrl();
  },

  /**
   * URL for the portrait registry endpoint
   */
  get registryUrl() {
    return `${this.baseUrl}/portraits/registry`;
  },

  /**
   * URL for the portraits list endpoint (lightweight)
   */
  get portraitsListUrl() {
    return `${this.baseUrl}/portraits`;
  },

  /**
   * URL for the title images list endpoint (lightweight)
   */
  get titleImagesListUrl() {
    return `${this.baseUrl}/title-images`;
  },

  /**
   * URL for the banners list endpoint (lightweight)
   */
  get bannersListUrl() {
    return `${this.baseUrl}/banners`;
  },

  /**
   * Get the direct URL for a portrait image
   * @param portraitId - The ID of the portrait
   * @param size - The size in pixels (64, 256, or 512)
   * @param format - The image format ('webp' or 'jpg')
   * @returns The full URL to the portrait image
   */
  getPortraitUrl: (portraitId: string, size: number, format: 'webp' | 'jpg'): string => {
    return `${imageServerConfig.baseUrl}/portraits/generated/${portraitId}-${size}.${format}`;
  },

  /**
   * Encode an image id for a single URL path segment (nested adventure ids contain `/`).
   */
  encodeImageIdForUrl: (imageId: string): string => encodeURIComponent(imageId),

  /**
   * Get the URL for a portrait using smart format negotiation
   * @param portraitId - The ID of the portrait (may contain colon for namespaced IDs)
   * @param variant - The variant size ('thumbnail', 'card', or 'large')
   * @returns The full URL for smart format negotiation endpoint
   */
  getPortraitVariantUrl: (portraitId: string, variant: 'thumbnail' | 'card' | 'large'): string => {
    const encodedId = imageServerConfig.encodeImageIdForUrl(portraitId);
    return `${imageServerConfig.baseUrl}/portraits/${encodedId}/${variant}`;
  },

  /**
   * Get the URL for a title image using smart format negotiation
   * @param titleImageId - The ID of the title image (may contain colon for namespaced IDs)
   * @param variant - The variant size ('thumbnail', 'card', or 'large')
   * @returns The full URL for smart format negotiation endpoint
   */
  getTitleImageUrl: (titleImageId: string, variant: 'thumbnail' | 'card' | 'large'): string => {
    const encodedId = imageServerConfig.encodeImageIdForUrl(titleImageId);
    return `${imageServerConfig.baseUrl}/title-images/${encodedId}/${variant}`;
  },

  /**
   * Get the URL for a banner using smart format negotiation
   * @param bannerId - The ID of the banner (may contain colon for namespaced IDs)
   * @param variant - The variant size ('thumbnail', 'card', or 'large')
   * @returns The full URL for smart format negotiation endpoint
   */
  getBannerUrl: (bannerId: string, variant: 'thumbnail' | 'card' | 'large'): string => {
    const encodedId = imageServerConfig.encodeImageIdForUrl(bannerId);
    return `${imageServerConfig.baseUrl}/banners/${encodedId}/${variant}`;
  },
};

export default imageServerConfig;

